import { ArrowBack as BackIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useApolloClient } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageLayout from "../../components/common/page-layout";
import {
  ApiKeyAdminPreviewMappingDocument,
  type ApiKeyAdminPreviewMappingQuery,
  type ApiKeyAdminPreviewMappingQueryVariables,
  WebhookPayloadShape,
  useApiKeyAdminExamplePayloadQuery,
  useApiKeyAdminGetQuery,
  useApiKeyAdminSetMappingTemplateMutation,
  useApiKeyAdminWebhookShapeConfigQuery,
} from "../../generated/graphql";
import useIsInternalAdmin from "./hooks/use-is-internal-admin";
import { formatPreviewError } from "./preview-error";
import { throwOnGraphqlErrors } from "./throw-on-graphql-errors";

const styles = {
  page: { p: 3 } satisfies SxProps<Theme>,
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 2,
  } satisfies SxProps<Theme>,
  controls: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 2,
    mb: 2,
  } satisfies SxProps<Theme>,
  shapeSelect: { minWidth: 260 } satisfies SxProps<Theme>,
  columns: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
    gap: 2,
  } satisfies SxProps<Theme>,
  monospaceInput: {
    fontFamily: "monospace",
    fontSize: 13,
  } satisfies SxProps<Theme>,
  centered: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    p: 4,
  } satisfies SxProps<Theme>,
};

const EDITOR_ROWS = 26;

/**
 * Admin page for authoring an API key's custom webhook mapping template for a
 * chosen payload shape. Three columns: the auto-generated (editable) example
 * default payload on the left, the transformed result in the center (produced
 * on demand by the "Apply" button), and the editable mapping template on the
 * right. The template can be saved back to the key's shape config; the result
 * uses the key's saved status mappings for `map:` pipes.
 */
const ApiKeyPayloadMappingPage = () => {
  const { keyUuid } = useParams<{ keyUuid: string }>();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const { canAccess, loading: permissionsLoading } = useIsInternalAdmin();

  const [shape, setShape] = useState<WebhookPayloadShape | "">("");
  const [example, setExample] = useState("");
  const [template, setTemplate] = useState("");
  const [result, setResult] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { data: keyData, loading: keyLoading } = useApiKeyAdminGetQuery({
    variables: { uuid: keyUuid ?? "" },
    skip: !canAccess || keyUuid === undefined,
    fetchPolicy: "cache-and-network",
  });

  const payloadShapes = useMemo(
    () => keyData?.apiKeyAdminGet.apiKey.payloadShapes ?? [],
    [keyData],
  );

  // Default the shape selector to the key's first configured payload shape.
  useEffect(() => {
    const first = payloadShapes[0];
    if (shape === "" && first !== undefined) {
      setShape(first);
    }
  }, [payloadShapes, shape]);

  // `activeShape` supplies a type-valid enum for the query variables; the query
  // itself is skipped until the user has actually selected a shape, so the
  // fallback value is never fetched.
  const activeShape =
    shape === "" ? WebhookPayloadShape.OrderStatusChange : shape;

  const { data: exampleData } = useApiKeyAdminExamplePayloadQuery({
    variables: {
      shape: activeShape,
      sendCompanyLocalTimestamps:
        keyData?.apiKeyAdminGet.apiKey.sendCompanyLocalTimestamps,
    },
    skip: shape === "",
    fetchPolicy: "cache-and-network",
  });

  const { data: configData } = useApiKeyAdminWebhookShapeConfigQuery({
    variables: { apiKeyUuid: keyUuid ?? "", shape: activeShape },
    skip: shape === "" || keyUuid === undefined,
    fetchPolicy: "cache-and-network",
  });

  // Reset the editable example whenever a fresh example is fetched (shape change).
  useEffect(() => {
    if (exampleData !== undefined) {
      setExample(exampleData.apiKeyAdminExamplePayload);
      setResult("");
    }
  }, [exampleData]);

  // Load the saved template into the editor whenever the config is (re)fetched.
  useEffect(() => {
    if (configData !== undefined) {
      setTemplate(
        configData.apiKeyAdminWebhookShapeConfig?.mappingTemplate ?? "",
      );
    }
  }, [configData]);

  const [saveTemplate, { loading: saving }] =
    useApiKeyAdminSetMappingTemplateMutation();

  const flashNotice = (message: string) => {
    setNotice(message);
    globalThis.setTimeout(() => {
      setNotice(null);
    }, 1500);
  };

  const handleApply = async () => {
    if (shape === "" || keyUuid === undefined) {
      return;
    }
    setPageError(null);
    setPreviewing(true);
    try {
      const response = await apolloClient.query<
        ApiKeyAdminPreviewMappingQuery,
        ApiKeyAdminPreviewMappingQueryVariables
      >({
        query: ApiKeyAdminPreviewMappingDocument,
        variables: {
          input: {
            apiKeyUuid: keyUuid,
            shape,
            mappingTemplate: template,
            examplePayload: example,
          },
        },
        fetchPolicy: "no-cache",
      });
      setResult(response.data.apiKeyAdminPreviewMapping);
    } catch (error_) {
      // Surface the failure (message + template location) in the result column.
      setResult(formatPreviewError(error_));
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    if (shape === "" || keyUuid === undefined) {
      return;
    }
    setPageError(null);
    try {
      const response = await saveTemplate({
        variables: {
          input: {
            apiKeyUuid: keyUuid,
            shape,
            mappingTemplate: template.trim() === "" ? null : template,
          },
        },
      });
      throwOnGraphqlErrors(response);
      flashNotice("Template saved");
    } catch (error_) {
      const message =
        error_ instanceof Error ? error_.message : "Unknown error";
      setPageError(message);
    }
  };

  const handleResetExample = () => {
    if (exampleData !== undefined) {
      setExample(exampleData.apiKeyAdminExamplePayload);
    }
  };

  if (permissionsLoading || (keyLoading && keyData === undefined)) {
    return (
      <PageLayout maxWidth={false} title="Payload mappings">
        <Box sx={styles.centered}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }
  if (!canAccess) {
    return (
      <PageLayout maxWidth={false} title="Payload mappings">
        <Alert severity="error">
          This tool requires the INTERNAL_TOOLS permission.
        </Alert>
      </PageLayout>
    );
  }

  const keyName = keyData?.apiKeyAdminGet.apiKey.name ?? "";

  return (
    <PageLayout
      maxWidth={false}
      subtitle={keyName}
      title="Payload mappings"
      actions={
        <Button
          size="small"
          startIcon={<BackIcon />}
          onClick={() => {
            navigate(`/admin/api-keys/${keyUuid ?? ""}`);
          }}
        >
          Back to key
        </Button>
      }
    >
      {pageError !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {pageError}
        </Alert>
      )}
      {notice !== null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}

      {payloadShapes.length === 0 ? (
        <Alert severity="info">
          This API key has no payload shapes configured. Add shapes in the edit
          dialog first.
        </Alert>
      ) : (
        <>
          <Box sx={styles.controls}>
            <TextField
              select
              size="small"
              label="Payload shape"
              value={shape}
              sx={styles.shapeSelect}
              onChange={(e) => {
                setShape(e.target.value as WebhookPayloadShape);
              }}
            >
              {payloadShapes.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              disabled={previewing || shape === ""}
              onClick={() => {
                void handleApply();
              }}
            >
              {previewing ? "Applying…" : "Apply →"}
            </Button>
            <Button
              variant="outlined"
              disabled={saving || shape === ""}
              onClick={() => {
                void handleSave();
              }}
            >
              {saving ? "Saving…" : "Save template"}
            </Button>
            <Button variant="text" onClick={handleResetExample}>
              Reset example
            </Button>
          </Box>

          <Box sx={styles.columns}>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Example payload</Typography>
              <TextField
                multiline
                fullWidth
                minRows={EDITOR_ROWS}
                maxRows={EDITOR_ROWS}
                value={example}
                InputProps={{ sx: styles.monospaceInput }}
                onChange={(e) => {
                  setExample(e.target.value);
                }}
              />
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Result</Typography>
              <TextField
                multiline
                fullWidth
                minRows={EDITOR_ROWS}
                maxRows={EDITOR_ROWS}
                value={result}
                placeholder='Press "Apply" to render the template against the example.'
                InputProps={{ readOnly: true, sx: styles.monospaceInput }}
              />
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Mapping template</Typography>
              <TextField
                multiline
                fullWidth
                minRows={EDITOR_ROWS}
                maxRows={EDITOR_ROWS}
                value={template}
                placeholder="JSON5 template with {{path|pipe}} placeholders"
                InputProps={{ sx: styles.monospaceInput }}
                onChange={(e) => {
                  setTemplate(e.target.value);
                }}
              />
            </Stack>
          </Box>
        </>
      )}
    </PageLayout>
  );
};

export default ApiKeyPayloadMappingPage;
